import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { PageShell } from "@/components/PageShell";
import NAPKINS from "@/data/napkins.json";

const SERIES = {
  "KSI-CN": "Classic Christmas",
  "KSI-PN": "Premium Print",
  "KSI-SN": "Shaped Napkins",
  "KSI-WN": "Winter Motif",
  "KSI-XN": "Signature",
};

const seriesOf = (code) => {
  const key = Object.keys(SERIES).find((k) => code.startsWith(k));
  return key ? SERIES[key] : "Christmas";
};

const FILTERS = [
  { key: "all", label: "All designs" },
  { key: "KSI-CN", label: "Classic Christmas" },
  { key: "KSI-PN", label: "Premium Print" },
  { key: "KSI-SN", label: "Shaped Napkins" },
  { key: "KSI-WN", label: "Winter Motif" },
  { key: "KSI-XN", label: "Signature" },
];

export default function ChristmasNapkins() {
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  const items = useMemo(() => {
    return NAPKINS.filter((n) => {
      if (filter === "all") return true;
      return n.code.startsWith(filter);
    });
  }, [filter]);

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-12">
        <Link
          to="/products"
          data-testid="back-to-products"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#5C665C] hover:text-[#1C2C22]"
        >
          <ArrowLeft size={14} /> All products
        </Link>

        <div className="mt-8 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="overline">Seasonal collection · 2026</p>
            <h1 className="mt-6 font-serif text-6xl md:text-8xl leading-[0.9] tracking-tighter">
              Christmas <span className="italic text-[#C27A62]">Napkins.</span>
            </h1>
          </div>
          <div className="md:col-span-5">
            <p className="text-lg text-[#5C665C] leading-relaxed">
              A festive edit of {NAPKINS.length} designs — from classic prints to
              gold-foil finishes and shaped napkins for holiday tables.
              Available in bulk for retail, hospitality and export.
            </p>
            <ul
              data-testid="napkin-specs"
              className="mt-8 grid grid-cols-2 gap-4 max-w-md"
            >
              {[
                { k: "Size", v: "32 × 32 cm" },
                { k: "Ply", v: "2-ply" },
                { k: "Finish", v: "Printed · Gold foil" },
                { k: "MOQ", v: "On request" },
              ].map((s) => (
                <li
                  key={s.k}
                  className="border-t border-[#D1C9B8] pt-3"
                >
                  <p className="overline">{s.k}</p>
                  <p className="font-serif text-xl mt-1 text-[#1C2C22]">
                    {s.v}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FILTER BAR */}
      <section
        data-testid="napkins-filter"
        className="border-y border-[#D1C9B8] bg-[#EBE5D9]/60 sticky top-20 z-20 backdrop-blur-md"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center gap-3">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`filter-${f.key.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
                filter === f.key
                  ? "bg-[#1C2C22] text-[#F4F1EB] border-[#1C2C22]"
                  : "border-[#D1C9B8] text-[#1C2C22] hover:border-[#1C2C22]"
              }`}
            >
              {f.label}
            </button>
          ))}
          <span
            data-testid="napkins-count"
            className="ml-auto text-xs uppercase tracking-[0.25em] text-[#5C665C]"
          >
            {items.length} designs
          </span>
        </div>
      </section>

      {/* GALLERY */}
      <section
        data-testid="napkins-grid"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-16"
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
          {items.map((n) => (
            <button
              key={n.code}
              data-testid={`napkin-${n.code}`}
              onClick={() => setLightbox(n)}
              className="group text-left"
            >
              <div className="aspect-square bg-[#EBE5D9] border border-[#D1C9B8] overflow-hidden relative">
                <img
                  src={n.image}
                  alt={`Napkin design ${n.code}`}
                  loading="lazy"
                  className="w-full h-full object-contain p-4 md:p-6 transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.2em] bg-[#F4F1EB] px-2 py-1 border border-[#D1C9B8]">
                  {seriesOf(n.code)}
                </span>
              </div>
              <div className="pt-4 flex items-baseline justify-between gap-3">
                <span className="font-serif text-xl tracking-tight text-[#1C2C22]">
                  {n.code}
                </span>
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#5C665C]">
                  {n.finish}
                </span>
              </div>
            </button>
          ))}
        </div>

        {items.length === 0 && (
          <p
            data-testid="napkins-empty"
            className="text-center text-[#5C665C] py-24"
          >
            No designs in this filter yet.
          </p>
        )}
      </section>

      {/* CTA BAND */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="bg-[#1C2C22] text-[#F4F1EB] p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <p className="overline text-[#8A9A86]">Ready to stock the season?</p>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 tracking-tight">
              Order the Christmas napkin collection.
            </h2>
          </div>
          <Link
            to="/contact"
            data-testid="napkins-cta-contact"
            className="inline-flex items-center gap-3 border border-[#F4F1EB]/60 px-7 py-4 text-xs uppercase tracking-[0.25em] btn-lift hover:bg-[#F4F1EB] hover:text-[#1C2C22]"
          >
            Request pricing & MOQ →
          </Link>
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightbox && (
        <div
          data-testid="napkin-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[60] bg-[#1C2C22]/85 backdrop-blur-md flex items-center justify-center p-6 cursor-zoom-out"
        >
          <div
            className="max-w-3xl w-full bg-[#F4F1EB] border border-[#D1C9B8]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="aspect-square bg-[#EBE5D9]">
              <img
                src={lightbox.image}
                alt={lightbox.code}
                className="w-full h-full object-contain p-6 md:p-10"
              />
            </div>
            <div className="p-6 md:p-8 flex items-center justify-between gap-6">
              <div>
                <p className="overline">{seriesOf(lightbox.code)}</p>
                <p className="font-serif text-3xl mt-2 tracking-tight">
                  {lightbox.code}
                </p>
                <p className="text-sm text-[#5C665C] mt-1">
                  Finish · {lightbox.finish}
                </p>
                <p className="text-sm text-[#5C665C]">
                  Size · 32 × 32 cm · 2-ply
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Link
                  to="/contact"
                  data-testid="lightbox-cta-contact"
                  className="bg-[#1C2C22] text-[#F4F1EB] px-5 py-3 text-xs uppercase tracking-[0.25em] btn-lift hover:bg-[#C27A62]"
                >
                  Enquire →
                </Link>
                <button
                  data-testid="lightbox-close"
                  onClick={() => setLightbox(null)}
                  className="text-xs uppercase tracking-[0.25em] text-[#5C665C] hover:text-[#1C2C22]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
